/**
 * Document Processing Page
 * 3-tab layout for doula billing document workflows:
 * - Tab 1: Member Profile Creation (insurance card scanning + member matching)
 * - Tab 2: Master Claims (per-member claims with visit line items)
 * - Tab 3: Full Claim Entry (OCR extraction + claims creation from scanned forms)
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { GatedTabContent } from '@/components/common/GatedTabContent';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import MemberProfileTab from '@/src/app/billing/document-processing/components/MemberProfileTab';
import MasterClaimsTab from '@/src/app/billing/document-processing/components/MasterClaimsTab';
import FullClaimEntryTab from '@/src/app/billing/document-processing/components/FullClaimEntryTab';

interface DocumentProcessingPageProps {
  onNavigate?: (tab: string) => void;
}

export default function DocumentProcessingPage({ onNavigate }: DocumentProcessingPageProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [activeTab, setActiveTab] = useState('member-profiles');
  useSubtabNavigation(setActiveTab, ['member-profiles', 'master-claims', 'full-claim-entry'] as const);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        {!tabbed && (
        <h1 className="text-2xl font-bold text-gray-900">
          {t('document_processing.title', 'Document Processing')}
        </h1>
        )}
        <p className="text-sm text-gray-500 mt-1">
          {t('document_processing.subtitle', 'Insurance card scanning, master claims, and OCR-based claim entry')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="member-profiles">Member Profile Creation</TabsTrigger>
          <TabsTrigger value="master-claims">Master Claims</TabsTrigger>
          <TabsTrigger value="full-claim-entry">Full Claim Entry</TabsTrigger>
        </TabsList>

        <TabsContent value="member-profiles">
          <GatedTabContent pageKey="document-processing" tabKey="member-profiles">
            <MemberProfileTab onNavigate={onNavigate} />
          </GatedTabContent>
        </TabsContent>

        <TabsContent value="master-claims">
          <GatedTabContent pageKey="document-processing" tabKey="master-claims">
            <MasterClaimsTab
              onNavigate={onNavigate}
              onSwitchTab={(tab) => setActiveTab(tab)}
            />
          </GatedTabContent>
        </TabsContent>

        <TabsContent value="full-claim-entry">
          <GatedTabContent pageKey="document-processing" tabKey="full-claim-entry">
            <FullClaimEntryTab onNavigate={onNavigate} />
          </GatedTabContent>
        </TabsContent>
      </Tabs>
    </div>
  );
}
